/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import debounce from 'lodash/debounce'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Button from '@mui/material/Button'
import { DEBOUNCE_DELAY } from 'constants/consts'
import { CREATE_QUESTION, GET_ONE_QUESTION, SEARCH_TEMPLATE_QUESTION } from 'queries/question'
import {
  findOneQuestion,
  findOneQuestion_findOneQuestion as Question,
  findOneQuestionVariables,
} from '__generatedTypes__/findOneQuestion'
import { CreateQuestionInput, QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { createQuestion, createQuestionVariables } from '__generatedTypes__/createQuestion'
import {
  searchTemplateQuestion,
  searchTemplateQuestionVariables,
  searchTemplateQuestion_searchTemplateQuestion_items as Template,
} from '__generatedTypes__/searchTemplateQuestion'
import Input from 'components/Input'
import Select from 'components/Select'
import { useHistory } from 'hooks/useHistory'
import { toast } from 'utils/toast'
import * as classes from './styles'

export type TemplateFormProps = {
  selectedQuestionType?: QuestionTypeEnum
  setTemplateData: (template: Question) => void
  getTemplateData: (templateName: string) => Promise<CreateQuestionInput>
  isLoadTemplateDisabled?: boolean
}

export const TemplateForm = ({
  selectedQuestionType,
  setTemplateData,
  getTemplateData,
  isLoadTemplateDisabled,
}: TemplateFormProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [templateName, setTemplateName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { populateHistoryWithUser } = useHistory()
  const [createTemplate] = useMutation<createQuestion, createQuestionVariables>(CREATE_QUESTION)
  const [loadTemplate] = useLazyQuery<findOneQuestion, findOneQuestionVariables>(GET_ONE_QUESTION)
  const templatesState = useQuery<searchTemplateQuestion, searchTemplateQuestionVariables>(SEARCH_TEMPLATE_QUESTION, {
    notifyOnNetworkStatusChange: true,
  })
  const templates = templatesState.data?.searchTemplateQuestion.items ?? []

  const handleTemplateInputChange = useCallback(
    debounce((searchString: string) => {
      templatesState.refetch({
        searchString,
        questionType: selectedQuestionType,
      })
    }, DEBOUNCE_DELAY),
    [selectedQuestionType]
  )

  useEffect(() => {
    templatesState.refetch({
      questionType: selectedQuestionType,
    })
  }, [selectedQuestionType])

  const handleLoadTemplateClick = useCallback(async () => {
    if (!selectedTemplate || submitting) {
      return
    }

    setSubmitting(true)
    const { data } = await loadTemplate({
      variables: {
        params: selectedTemplate.id,
      },
    })
    data?.findOneQuestion && setTemplateData(data.findOneQuestion)
    setSubmitting(false)
  }, [setTemplateData, submitting, loadTemplate, selectedTemplate])

  const handleSaveTemplateClick = useCallback(async () => {
    if (!templateName || submitting) {
      return
    }

    setSubmitting(true)
    try {
      const templateData = await getTemplateData(templateName)
      await createTemplate({
        variables: {
          params: {
            ...templateData,
            history: populateHistoryWithUser(),
          },
        },
      })
      await templatesState.refetch()

      toast.success('Template saved successfully')
    } catch (error) {
      toast.error('Failed to save template')
    }
    setSubmitting(false)
  }, [getTemplateData, submitting, createTemplate, templateName, templatesState.refetch])

  return (
    <>
      <h4>Template</h4>
      <Select<Template>
        value={selectedTemplate}
        items={templates}
        onChange={setSelectedTemplate}
        onInputChange={handleTemplateInputChange}
        getItemLabel={(template) => template.templateName ?? 'No name'}
        renderItem={(template) => template.templateName ?? 'No name'}
        loading={templatesState.loading}
        disabled={isLoadTemplateDisabled}
        placeholder="Choose Template"
        displaySelectedItemsFirst
        searchable
        fullWidth
      />
      <Button
        variant="outlined"
        color="blue"
        type="button"
        onClick={handleLoadTemplateClick}
        disabled={!selectedTemplate || submitting || isLoadTemplateDisabled}
      >
        Load Template
      </Button>
      <div css={classes.divider} />
      <Input
        value={templateName}
        onChange={setTemplateName}
        label="Save as Template"
        placeholder="Enter Template Title"
        fullWidth
      />
      <Button
        variant="outlined"
        color="blue"
        type="button"
        onClick={handleSaveTemplateClick}
        disabled={!templateName || submitting}
      >
        Save
      </Button>
    </>
  )
}
